import { getCurrentUser } from './localDatabase';
import { addAuditLog } from './auditLog';

// Microsoft Graph API base URL
const GRAPH_API_BASE = 'https://graph.microsoft.com/v1.0';

// Store the access token
let accessToken: string | null = null;

/**
 * Sets the Microsoft Graph access token
 */
export const setMicrosoftAccessToken = (token: string) => {
  accessToken = token;
  localStorage.setItem('ms_access_token', token);
};

/**
 * Gets the Microsoft Graph access token
 */
export const getMicrosoftAccessToken = (): string | null => {
  if (!accessToken) {
    accessToken = localStorage.getItem('ms_access_token');
  }
  return accessToken;
};

/**
 * Clears the Microsoft Graph access token
 */
export const clearMicrosoftAccessToken = () => {
  accessToken = null;
  localStorage.removeItem('ms_access_token');
};

/**
 * Checks if the user is connected to Microsoft
 */
export const isMicrosoftConnected = (): boolean => {
  return !!getMicrosoftAccessToken();
};

/**
 * Gets the current user's Microsoft profile
 */
export const getMicrosoftProfile = async () => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/me`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to get Microsoft profile');
  }

  return await response.json();
};

/**
 * Uploads a file to Microsoft OneDrive
 */
export const uploadFileToOneDrive = async (
  file: File,
  folderPath: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  // Check if folder exists
  const folderResponse = await fetch(`${GRAPH_API_BASE}/me/drive/root:/${folderPath}:/children`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!folderResponse.ok && folderResponse.status !== 404) {
    throw new Error('Failed to check folder in OneDrive');
  }

  // If folder doesn't exist (404), create it
  if (folderResponse.status === 404) {
    // Split the path and create each folder level if needed
    const pathParts = folderPath.split('/').filter(Boolean);
    let currentPath = '';
    
    for (const part of pathParts) {
      currentPath += `/${part}`;
      
      // Check if this folder level exists
      const checkResponse = await fetch(`${GRAPH_API_BASE}/me/drive/root:${currentPath}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // If it doesn't exist, create it
      if (!checkResponse.ok) {
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        const folderName = part;
        
        const createResponse = await fetch(
          `${GRAPH_API_BASE}/me/drive/root:${parentPath}:/children`, 
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: folderName,
              folder: {},
              '@microsoft.graph.conflictBehavior': 'rename'
            })
          }
        );
        
        if (!createResponse.ok) {
          throw new Error(`Failed to create folder "${folderName}" in OneDrive`);
        }
      }
    }
  }

  // Upload file
  const fileName = file.name;
  const uploadUrl = `${GRAPH_API_BASE}/me/drive/root:/${folderPath}/${fileName}:/content`;

  // For small files (< 4MB), use simple upload
  if (file.size < 4 * 1024 * 1024) {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type
      },
      body: file
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to OneDrive');
    }

    const result = await response.json();
    
    // Log the upload
    addAuditLog('create', 'file', `File uploaded to OneDrive: ${fileName}`, result.id);
    
    return result.webUrl;
  } else {
    // For larger files, use upload session
    const createSessionResponse = await fetch(`${GRAPH_API_BASE}/me/drive/root:/${folderPath}/${fileName}:/createUploadSession`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        item: {
          '@microsoft.graph.conflictBehavior': 'rename'
        }
      })
    });

    if (!createSessionResponse.ok) {
      throw new Error('Failed to create upload session');
    }

    const sessionData = await createSessionResponse.json();
    const uploadUrl = sessionData.uploadUrl;

    // Upload the file in chunks
    const chunkSize = 1024 * 1024 * 4; // 4MB chunks
    const fileSize = file.size;
    let start = 0;

    while (start < fileSize) {
      const end = Math.min(start + chunkSize, fileSize);
      const chunk = file.slice(start, end);

      const chunkResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': `${end - start}`,
          'Content-Range': `bytes ${start}-${end - 1}/${fileSize}`
        },
        body: chunk
      });

      if (!chunkResponse.ok && chunkResponse.status !== 202) {
        throw new Error('Failed to upload file chunk');
      }

      // Update progress
      if (onProgress) {
        onProgress((end / fileSize) * 100);
      }

      // If this is the last chunk, get the file URL
      if (end === fileSize) {
        const result = await chunkResponse.json();
        
        // Log the upload
        addAuditLog('create', 'file', `File uploaded to OneDrive: ${fileName}`, result.id);
        
        return result.webUrl;
      }

      start = end;
    }

    throw new Error('Failed to complete file upload');
  }
};

/**
 * Gets a list of files from a OneDrive folder
 */
export const getFilesFromOneDrive = async (folderPath: string) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/me/drive/root:/${folderPath}:/children`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to get files from OneDrive');
  }

  const data = await response.json();
  return data.value;
};

/**
 * Exports building data to Excel in OneDrive
 */
export const exportBuildingToExcel = async (buildingId: string, buildingName: string) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const user = getCurrentUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Create a workbook using Microsoft Graph API
  const workbookResponse = await fetch(`${GRAPH_API_BASE}/me/drive/root/children`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: `${buildingName}.xlsx`,
      file: {},
      '@microsoft.graph.conflictBehavior': 'rename'
    })
  });

  if (!workbookResponse.ok) {
    throw new Error('Failed to create Excel file in OneDrive');
  }

  const workbook = await workbookResponse.json();
  
  // Log the export
  addAuditLog('export', 'building', `Building exported to Excel: ${buildingName}`, buildingId);
  
  return workbook.webUrl;
};

/**
 * Shares a file from OneDrive
 */
export const shareFileFromOneDrive = async (fileId: string) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/me/drive/items/${fileId}/createLink`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      type: 'view',
      scope: 'anonymous'
    })
  });

  if (!response.ok) {
    throw new Error('Failed to share file from OneDrive');
  }

  const data = await response.json();
  return data.link;
};

/**
 * Creates a Teams meeting
 */
export const createTeamsMeeting = async (
  subject: string,
  startDateTime: string,
  endDateTime: string,
  attendees: string[]
) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/me/events`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      subject,
      start: {
        dateTime: startDateTime,
        timeZone: 'UTC'
      },
      end: {
        dateTime: endDateTime,
        timeZone: 'UTC'
      },
      attendees: attendees.map(email => ({
        emailAddress: {
          address: email
        },
        type: 'required'
      })),
      isOnlineMeeting: true,
      onlineMeetingProvider: 'teamsForBusiness'
    })
  });

  if (!response.ok) {
    throw new Error('Failed to create Teams meeting');
  }

  const meeting = await response.json();
  
  // Log the meeting creation
  addAuditLog('create', 'meeting', `Teams meeting created: ${subject}`);
  
  return meeting;
};

/**
 * Gets upcoming meetings from Outlook calendar
 */
export const getUpcomingMeetings = async () => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const now = new Date().toISOString();
  const response = await fetch(`${GRAPH_API_BASE}/me/events?$filter=start/dateTime ge '${now}'&$orderby=start/dateTime&$top=10`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to get upcoming meetings');
  }

  const data = await response.json();
  return data.value;
};

/**
 * Uploads a document to SharePoint
 */
export const uploadToSharePoint = async (
  file: File,
  siteId: string,
  libraryName: string = 'Documents',
  folderPath: string = ''
) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  // Construct the path
  let path = `${libraryName}`;
  if (folderPath) {
    path += `/${folderPath}`;
  }

  // Upload the file
  const response = await fetch(`${GRAPH_API_BASE}/sites/${siteId}/drive/root:/${path}/${file.name}:/content`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': file.type
    },
    body: file
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to SharePoint');
  }

  const result = await response.json();
  
  // Log the upload
  addAuditLog('create', 'file', `File uploaded to SharePoint: ${file.name}`, result.id);
  
  return result;
};

/**
 * Gets available SharePoint sites
 */
export const getSharePointSites = async () => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/sites?search=*`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to get SharePoint sites');
  }

  const data = await response.json();
  return data.value;
};

/**
 * Sends a message to a Teams channel
 */
export const sendTeamsMessage = async (
  teamId: string,
  channelId: string,
  message: string
) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  const response = await fetch(`${GRAPH_API_BASE}/teams/${teamId}/channels/${channelId}/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      body: {
        content: message,
        contentType: 'html'
      }
    })
  });

  if (!response.ok) {
    throw new Error('Failed to send Teams message');
  }

  const result = await response.json();
  
  // Log the message
  addAuditLog('create', 'message', `Teams message sent to channel ${channelId}`);
  
  return result;
};

/**
 * Gets available Teams and channels
 */
export const getTeamsAndChannels = async () => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  // Get teams
  const teamsResponse = await fetch(`${GRAPH_API_BASE}/me/joinedTeams`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!teamsResponse.ok) {
    throw new Error('Failed to get Teams');
  }

  const teamsData = await teamsResponse.json();
  const teams = teamsData.value;

  // Get channels for each team
  const teamsWithChannels = await Promise.all(
    teams.map(async (team: any) => {
      const channelsResponse = await fetch(`${GRAPH_API_BASE}/teams/${team.id}/channels`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!channelsResponse.ok) {
        return {
          ...team,
          channels: []
        };
      }

      const channelsData = await channelsResponse.json();
      return {
        ...team,
        channels: channelsData.value
      };
    })
  );

  return teamsWithChannels;
};

/**
 * Creates a Power BI report from building data
 * Note: This is a simplified implementation that would need to be expanded
 * with actual Power BI REST API calls in a real application
 */
export const createPowerBIReport = async (buildingId: string, buildingName: string) => {
  const token = getMicrosoftAccessToken();
  if (!token) {
    throw new Error('Not connected to Microsoft');
  }

  // This is a placeholder for actual Power BI integration
  // In a real implementation, you would:
  // 1. Export the building data to a format Power BI can use
  // 2. Use the Power BI REST API to create a dataset
  // 3. Create a report based on that dataset
  
  // For now, we'll just log the action and return a mock URL
  addAuditLog('create', 'report', `Power BI report created for building: ${buildingName}`, buildingId);
  
  return `https://app.powerbi.com/reports/mock-report-${buildingId}`;
};